import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { BERLIN_TIME_ZONE } from '@e3dc-react/shell/Libs/date'
import { ChartView, ChartViews } from '../../chart-kit/shared/utils/chartModel'

export type TimeRange = {
  from: number
  to: number
}

export const getFetchRanges = (from: Moment, to: Moment): TimeRange[] => {
  if (to.isBefore(from) || to.isSame(from)) return []

  const maxTo = moment().utc().endOf('month').isBefore(to) ? moment().utc().endOf('month') : to
  const ranges = splitTimeRange(from, maxTo)
  if (ranges.length > 1) {
    return ranges.map(({ from, to }) => ({ from: moment(from).utc().valueOf(), to: moment(to).utc().valueOf() }))
  }

  //if only one month is requested, add prev month also to have reference values for forecast
  const prevMonthFrom = moment(ranges[0].from).subtract(1, 'month')
  return [{ from: prevMonthFrom, to: ranges[0].from }, ...ranges].map(({ from, to }) => ({
    from: moment(from).utc().valueOf(),
    to: moment(to).utc().valueOf(),
  }))
}

/**
 * Find all Months inside given timerange
 * @param from Start of given timerange
 * @param to end of given timerange
 * @returns Array of Timeranges {from, to}, each range is one month
 */
export const splitTimeRange = (from: Moment, to: Moment): TimeRange[] => {
  if (to.isBefore(from) || to.isSame(from)) return []

  const start = from.clone().startOf('month')
  const ranges = []
  let current = start
  do {
    const next = current.clone().add(1, 'month')
    ranges.push({ from: current.valueOf(), to: next.valueOf() })
    current = next
  } while (current.isBefore(to))

  return ranges
}

/**
 * @deprecated
 * @param start
 * @param end
 * @param toCheck
 */
export const isBetweenInclusive = (start: Moment, end: Moment, toCheck: Moment): boolean => {
  return !(toCheck.isAfter(end) || toCheck.isBefore(start))
}

export const isStartOfDay = (timestamp: Moment): boolean => {
  return timestamp.isSame(timestamp.clone().startOf('day'))
}

export const isStartOfHour = (timestamp: Moment): boolean => {
  return timestamp.isSame(timestamp.clone().startOf('hour'))
}

export const isStartOfWeek = (timestamp: Moment): boolean => {
  return timestamp.isSame(timestamp.clone().startOf('week'))
}

export const isStartOfMonth = (timestamp: Moment): boolean => {
  return timestamp.isSame(timestamp.clone().startOf('month'))
}

export const isStartOfYear = (timestamp: Moment): boolean => {
  return timestamp.isSame(timestamp.clone().startOf('year'))
}

export const getDSTValueFromRange = (from: number, to: number): number | undefined => {
  const currentTs = moment(from)

  while (currentTs.isBefore(moment(to))) {
    const next = currentTs.clone().add(1, 'hour')

    if ((!currentTs.isDST() && next.isDST()) || (currentTs.isDST() && !next.isDST())) {
      return next.valueOf()
    }
    currentTs.add(1, 'hour')
  }
  return undefined
}

export const getMaxToDateValue = (view: ChartView): Moment => {
  if (view === 'year') return moment().endOf('month')
  /** TODO: revert CPE-2043 changes, if reverted update tests in @file datetime.test.ts */
  // if (view === 'day') return moment().add(1, 'day').endOf('day')
  return moment().add(1, view)
}

/**
 * Calculate the maximum date value for a given view.
 *
 * @param view The type of chart view (year, week, or day).
 * @param rangeDate boolean for checking if the date is within a range.
 * @param from The input moment.
 * @returns The maximum date value for the given inputs after handling the moment past or present.
 * with special case if the view was a day its allowed to have 1 day forecast.
 */
export const getMaxFromDateValue = (view: ChartView, rangeDate: boolean, from?: Moment): Moment => {
  if (rangeDate && view === 'year') return moment().startOf('month')
  if (!rangeDate && from && view === 'day') {
    return moment().add(1, 'day').startOf('day')
  }

  return moment().startOf(view)
}

export const getToDateValue = (from: Moment, view: ChartView, hasCustomRange = false): Moment => {
  const isDayView = view === ChartViews.Day
  if (hasCustomRange && !isDayView) return from.clone().endOf(view)
  return from.clone().add(1, view)
}

/**
 * The history routes expect `to` as an exclusive bound at local midnight (00:00:00 CET).
 * A value carrying a time component (e.g. the 23:59:59.999 of `endOf(view)`) is rounded up to the
 * next midnight so the selected period stays fully covered.
 */
export const getMidnightToDateValue = (to: Moment): Moment => {
  const toInExportTimezone = to.clone().tz(BERLIN_TIME_ZONE)
  const startOfDay = toInExportTimezone.clone().startOf('day')
  return toInExportTimezone.isSame(startOfDay) ? startOfDay : startOfDay.add(1, 'day')
}
